import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import {
  Row,
  Col,
  Tag,
  Button,
  Table,
  Modal,
  Input,
  message,
  Checkbox,
  Radio,
  Select,
} from "antd"
import { Link } from "react-router-dom"
import MeetEntryButton from "./MeetEntryButton"
import { swimminglyApi, gen } from "./utils/helperFunctions"
import type { IDeclarationsReportSwimmer } from "./MeetActionsModal" // type import
import {
  downloadDeclarationsCsv,
  downloadDeclarationsPdf,
  formatDeclarationAudit,
  deriveDeclarationStatus,
  compareDeclarationsByStatusThenName,
  downloadRelayAvailabilityPdf,
} from "./MeetDeclarationsUtils"

export type DeclarationsPhase = "before" | "open" | "closed"

interface MeetDeclarationsTabProps {
  meetId: number
  meetName: string
  dateLabel: string
  courseLabel: string
  impersonateClubName: string
  impersonateClubId: number | null
  userId: number | null
  meetStatus: number | null
  declarationsPhase: DeclarationsPhase
  meetDeclarationsCloseDate: string | null
  parentEntries: boolean
  parentAllowIndividualEntries: boolean
  parentAllowRelayAvailability: boolean
  parentAllowComments: boolean
  declarationsReportLoading: boolean
  declarationsReportSorted: IDeclarationsReportSwimmer[]
  declarationsReportSwimmers: IDeclarationsReportSwimmer[]
  onRefreshTrigger: () => void
  meetDeadlineUpperIso?: string | null
}

const MeetDeclarationsTab: React.FC<MeetDeclarationsTabProps> = ({
  meetId,
  meetName,
  dateLabel,
  courseLabel,
  impersonateClubName,
  impersonateClubId,
  userId,
  meetStatus,
  declarationsPhase,
  meetDeclarationsCloseDate,
  parentEntries,
  parentAllowIndividualEntries,
  parentAllowRelayAvailability,
  parentAllowComments,
  declarationsReportLoading,
  declarationsReportSorted,
  declarationsReportSwimmers,
  onRefreshTrigger,
  meetDeadlineUpperIso,
}) => {
  const canManage = Boolean(impersonateClubId && userId)

  const meetIsPublishedOrLater = meetStatus !== null && meetStatus >= 2

  // Declarations UI is only editable when the meet is in Editing Entries
  // state (meetStatus 0 or null). For any other status, we show everything
  // but disable controls.
  const declarationsLocked = meetStatus !== null && meetStatus !== 0

  const [emailModalVisible, setEmailModalVisible] = useState(false)
  const [emailDryRunSummary, setEmailDryRunSummary] = useState<{
    remindersSentCount: number
    maxRemindersPerMeet: number
  } | null>(null)
  const [emailSending, setEmailSending] = useState(false)
  const [testOverrideEmail, setTestOverrideEmail] = useState("")
  const [allowReplies, setAllowReplies] = useState(false)
  const [copyOtherClubAdmins, setCopyOtherClubAdmins] = useState(false)
  const [adminCopyLoading, setAdminCopyLoading] = useState(false)
  const [adminCopyOptions, setAdminCopyOptions] = useState<
    { label: string; value: number }[]
  >([])
  const [adminCopyUserIds, setAdminCopyUserIds] = useState<number[]>([])

  type RelayGenderFilter = "both" | "girls" | "boys"

  const [relayPdfModalVisible, setRelayPdfModalVisible] = useState(false)
  const [relayGenderFilter, setRelayGenderFilter] = useState<RelayGenderFilter>("both")
  const [relaySelectedAgeGroups, setRelaySelectedAgeGroups] = useState<string[]>([])

  const isNonProdClientEnv = (() => {
    if (typeof window === "undefined") return false
    const host = window.location.hostname || ""
    const isDev = host === "localhost" || host === "127.0.0.1"
    const isStaging = host.includes("swimminglytesting.ninja")
    return isDev || isStaging
  })()

  const statusCounts = useMemo(() => {
    let attending = 0
    let notAttending = 0
    let noResponse = 0

    declarationsReportSorted.forEach((swimmer) => {
      const status = deriveDeclarationStatus(swimmer)
      if (status === "attending") attending += 1
      else if (status === "notAttending") notAttending += 1
      else noResponse += 1
    })

    return {
      total: declarationsReportSorted.length,
      attending,
      notAttending,
      noResponse,
    }
  }, [declarationsReportSorted])

  const getAgeGroupSortKey = (ageGroup: string | null | undefined): number => {
    if (!ageGroup || typeof ageGroup !== "string") return Number.MAX_SAFE_INTEGER
    const match = ageGroup.match(/\d+/)
    if (!match) return Number.MAX_SAFE_INTEGER
    return parseInt(match[0], 10)
  }

  const relayAgeGroupOptions = useMemo(
    () => {
      const ageSet = new Set<string>()

      declarationsReportSwimmers.forEach((s) => {
        s.elligibleEvents?.forEach((evt) => {
          if (evt.isRelay === 1 && evt.age_group) {
            ageSet.add(evt.age_group)
          }
        })
      })

      const ages = Array.from(ageSet)
      ages.sort((a, b) => {
        const keyA = getAgeGroupSortKey(a)
        const keyB = getAgeGroupSortKey(b)
        if (keyA !== keyB) return keyA - keyB
        return a.localeCompare(b)
      })

      return ages.map((age) => ({ label: age, value: age }))
    },
    [declarationsReportSwimmers],
  )

  const hasRelayAvailabilityData = useMemo(
    () =>
      declarationsReportSwimmers.some((s) => {
        if (deriveDeclarationStatus(s) !== "attending") return false
        return (
          s.elligibleEvents?.some(
            (evt) => evt.isRelay === 1 && (evt.relayAvailable === 0 || evt.relayAvailable === 1),
          ) || false
        )
      }),
    [declarationsReportSwimmers],
  )

  useEffect(() => {
    if (!relayPdfModalVisible) return
    if (relaySelectedAgeGroups.length > 0) return
    if (relayAgeGroupOptions.length === 0) return

    setRelaySelectedAgeGroups(relayAgeGroupOptions.map((opt) => opt.value as string))
  }, [relayPdfModalVisible, relaySelectedAgeGroups, relayAgeGroupOptions])

  const sortedForDisplay = useMemo(
    () => [...declarationsReportSorted].sort(compareDeclarationsByStatusThenName),
    [declarationsReportSorted],
  )

  const handleExportCsv = () => {
    if (!impersonateClubId || !canManage) return

    downloadDeclarationsCsv(sortedForDisplay, {
      meetName,
      dateLabel,
      courseLabel,
      clubLabel: impersonateClubName,
    })
  }

  const handleExportPdf = () => {
    if (!impersonateClubId || !canManage) return

    downloadDeclarationsPdf(sortedForDisplay, {
      meetName,
      dateLabel,
      courseLabel,
      clubLabel: impersonateClubName,
    })
  }

  const handleOpenRelayPdfModal = () => {
    setRelayPdfModalVisible(true)
  }

  const handleDownloadRelayPdf = () => {
    if (!impersonateClubId || !canManage) return

    if (relaySelectedAgeGroups.length === 0) {
      message.error("Please select at least one age group.")
      return
    }

    downloadRelayAvailabilityPdf(declarationsReportSwimmers, {
      meetName,
      dateLabel,
      courseLabel,
      clubLabel: impersonateClubName,
      selectedAgeGroups: relaySelectedAgeGroups,
      genderFilter: relayGenderFilter,
    })

    setRelayPdfModalVisible(false)
  }

  const handleOpenEmailNoResponse = () => {
    setEmailModalVisible(true)
    setEmailDryRunSummary(null)

    // Fire a dry-run request to get counts (no filters for now)
    fetch(
      `/api/meetDeclarations/emailNoResponse`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meetId,
          clubId: impersonateClubId,
          userId,
          dryRun: true,
        }),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" || data.status === "throttled") {
          setEmailDryRunSummary({
            remindersSentCount: data.remindersSentCount ?? 0,
            maxRemindersPerMeet: data.maxRemindersPerMeet ?? 3,
          })
        }
      })
      .catch(() => {
        // ignore for now; UI will just not show summary
      })
  }

  const handleSendEmailNoResponse = () => {
    if (!impersonateClubId) return
    setEmailSending(true)

    let overrideAddress: string | undefined
    if (isNonProdClientEnv && testOverrideEmail.trim()) {
      const trimmed = testOverrideEmail.trim()
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      if (!isValid) {
        message.error("Please enter a valid email address.")
        setEmailSending(false)
        return
      }
      overrideAddress = trimmed
    }

    fetch(`/api/meetDeclarations/emailNoResponse`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        meetId,
        clubId: impersonateClubId,
        userId,
        dryRun: false,
        allowResponses: allowReplies,
        copyOtherClubAdmins,
        adminCopyUserIds,
        testOverrideToAddress: overrideAddress,
      }),
    })
      .then((res) => res.json())
      .then(() => {
        setEmailModalVisible(false)
      })
      .finally(() => {
        setEmailSending(false)
      })
  }

  const loadAdminCopyOptions = async () => {
    if (!impersonateClubId || !userId) return
    try {
      setAdminCopyLoading(true)
      const response = await swimminglyApi.get(
        gen(
          `/api/meetDeclarations/copyAdmins?clubId=${encodeURIComponent(
            String(impersonateClubId),
          )}&userId=${encodeURIComponent(String(userId))}`,
        ),
      )
      const data: any = (response as any)?.data ?? response ?? {}
      const admins: any[] = Array.isArray(data.admins) ? data.admins : []
      setAdminCopyOptions(
        admins.map((admin) => ({
          value: Number(admin.userId),
          label: `${admin.name} (${admin.roleName || "Admin"})`,
        })),
      )
    } catch (_err) {
      message.error("Unable to load club admin recipients")
    } finally {
      setAdminCopyLoading(false)
    }
  }

  const handleCopyAdminsToggle = (checked: boolean) => {
    setCopyOtherClubAdmins(checked)
    if (!checked) {
      setAdminCopyUserIds([])
      return
    }
    if (adminCopyOptions.length === 0) {
      void loadAdminCopyOptions()
    }
  }

  const remindersSentCount = emailDryRunSummary?.remindersSentCount ?? 0
  const maxRemindersPerMeet = emailDryRunSummary?.maxRemindersPerMeet ?? 3
  const remainingReminders = Math.max(0, maxRemindersPerMeet - remindersSentCount)
  const atReminderLimit = remindersSentCount >= maxRemindersPerMeet
  const isProdLikeClientEnv = !isNonProdClientEnv

  return (
    <>
      {declarationsLocked && (
        <div
          className="shadowed"
          style={{
            marginBottom: 12,
            padding: 8,
            backgroundColor: "#fffbe6",
            borderColor: "#ffe58f",
            borderStyle: "solid",
            borderWidth: 1,
            fontSize: 12,
          }}
        >
          Meet Declarations functionality is locked once the heat sheet has been
          merged. You can still reference the past meet
          declaration report below for future use.
        </div>
      )}
      <div style={{ position: "relative" }}>
        <div className="shadowed">
          {canManage ? (
            <Row
              gutter={[16, 16]}
              style={declarationsLocked ? { opacity: 0.5 } : undefined}
            >
              <Col xs={24} md={14}>
                <div
                  style={{
                    fontSize: 12,
                    color: "#101242",
                    fontFamily:
                      "'Museo Sans Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  }}
                >
                <div style={{ marginBottom: 8 }}>
                  {declarationsPhase === "before" && (
                    <Tag color="default">Not yet opened to parents</Tag>
                  )}
                  {declarationsPhase === "open" && (
                    <Tag color="green">Open to parents</Tag>
                  )}
                  {declarationsPhase === "closed" && (
                    <Tag color="red">Closed to parents</Tag>
                  )}
                </div>

                {declarationsPhase === "before" && (
                  <>
                    <p style={{ marginBottom: 4 }}>
                      Use Meet Declarations to collect who is attending this meet before you
                      finalize the lineup.
                    </p>
                    <ul style={{ paddingLeft: 18, marginBottom: 4 }}>
                      <li>
                        Opening declarations sends an email to parent/guardians with this meet's
                        details and a direct link to respond.
                      </li>
                      <li>
                        Parents can always mark each swimmer as attending or not attending.
                        You control whether they can also help with individual event entries,
                        relay availability, and comments.
                      </li>
                      <li>
                        Use the toggles on the right to allow parents to enter individual
                        events, mark relay availability, and send comments to coaches for this
                        meet/club.
                      </li>
                      <li>
                        Parent/Guardians can leave comments for the coaching staff, reviewable in the meet declarations report below when that option is enabled.
                      </li>
                      <li>
                        You can always review and edit swimmer status and entries from View Meet
                        Lineup.
                      </li>
                    </ul>
                    <p style={{ marginBottom: 0 }}>
                      We recommend setting a declarations deadline a few days before the meet so
                      staff has time to review and finalize entries.
                    </p>
                  </>
                )}

                {declarationsPhase === "open" && (
                  <>
                    <p style={{ marginBottom: 4 }}>
                      Meet Declarations are currently open to parents & guardians
                      {meetDeclarationsCloseDate && (
                        <>
                          {" "}
                          until{" "}
                          <strong>
                            {dayjs(meetDeclarationsCloseDate).local().format("M/D h:mm A")}
                          </strong>
                          .
                        </>
                      )}
                    </p>
                    <ul style={{ paddingLeft: 18, marginBottom: 4 }}>
                      <li>
                        Parents can still update attendance and{" "}
                        {parentEntries
                          ? "event selections until the deadline."
                          : "you can continue refining entries as an admin."}
                      </li>
                      <li>
                        Use View Meet Lineup to monitor who is attending, not attending, or has
                        not responded.
                      </li>
                    </ul>
                    <p style={{ marginBottom: 0 }}>
                      If you need to adjust the deadline or close declarations early, use the
                      controls on the right.
                    </p>
                  </>
                )}

                {declarationsPhase === "closed" && (
                  <>
                    <p style={{ marginBottom: 4 }}>
                      Meet Declarations are closed to parents
                      {meetDeclarationsCloseDate && (
                        <>
                          {" "}
                          as of{" "}
                          <strong>
                            {dayjs(meetDeclarationsCloseDate).local().format("M/D h:mm A")}
                          </strong>
                          .
                        </>
                      )}
                    </p>
                    <ul style={{ paddingLeft: 18, marginBottom: 4 }}>
                      <li>
                        Parents can no longer change responses, but club admins can still adjust
                        entries from View Meet Lineup.
                      </li>
                      <li>
                        You can reopen declarations with a new deadline if you need to collect more
                        responses or corrections.
                      </li>
                    </ul>
                    <p style={{ marginBottom: 0 }}>
                      Use the controls on the right if you'd like to reopen or extend
                      declarations.
                    </p>
                  </>
                )}
                </div>
              </Col>
              <Col xs={24} md={10}>
                {impersonateClubId && userId && (
                  <>
                    <MeetEntryButton
                      className="shadowed item"
                      meetId={meetId}
                      clubId={impersonateClubId}
                      userId={userId}
                      refreshPage={onRefreshTrigger}
                      meetDeadlineUpperIso={meetDeadlineUpperIso}
                      parentAllowIndividualEntries={parentAllowIndividualEntries}
                      parentAllowRelayAvailability={parentAllowRelayAvailability}
                      parentAllowComments={parentAllowComments}
                      declarationsLocked={declarationsLocked}
                    />
                    <div style={{ marginTop: 8, fontSize: 11, color: "#666" }}>
                      Club declarations deadlines are capped by the earlier of the meet entries deadline and meet start.
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11 }}>
                      {meetIsPublishedOrLater || declarationsLocked ? (
                        <span style={{ color: "#999" }}>
                          View 'Edit Entries' to see swimmer-by-swimmer declarations and entries
                        </span>
                      ) : (
                        <Link to={`/app/club/viewMeetLineUp2/${meetId}`}>
                          View 'Edit Entries' to see swimmer-by-swimmer declarations and entries
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </Col>
            </Row>
          ) : (
            <p style={{ fontSize: 12, margin: 0 }}>
              Meet declarations are only available when managing a specific club.
            </p>
          )}
        </div>

        {canManage ? (
          declarationsPhase === "before" ? (
            <div className="shadowed" style={{ marginTop: 12 }}>
              <p style={{ fontSize: 12, margin: 0 }}>
                Declarations report will appear here once declarations are opened.
              </p>
            </div>
          ) : (
            <div className="shadowed" style={{ marginTop: 12 }}>
              <>
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#101242",
                      fontFamily:
                        "'Museo Sans Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      opacity: declarationsLocked ? 0.5 : 1,
                    }}
                  >
                    <div style={{ marginBottom: 4 }}>
                      Real-time view of every swimmer in your club for this meet, including whether
                      they're attending and how many individual events they're entered in.
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span>
                        <strong>{statusCounts.total}</strong> swimmers
                      </span>
                      <Tag color="green">Attending: {statusCounts.attending}</Tag>
                      <Tag color="red">Not attending: {statusCounts.notAttending}</Tag>
                      <Tag>No response: {statusCounts.noResponse}</Tag>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      position: "relative",
                      zIndex: declarationsLocked ? 3 : "auto",
                    }}
                  >
                    <Button
                      className="declarations-export-button"
                      size="small"
                      onClick={handleExportCsv}
                      disabled={
                        declarationsReportLoading ||
                        declarationsReportSwimmers.length === 0
                      }
                    >
                      Export CSV
                    </Button>
                    <Button
                      className="declarations-export-button"
                      size="small"
                      onClick={handleExportPdf}
                      disabled={
                        declarationsReportLoading ||
                        declarationsReportSwimmers.length === 0
                      }
                    >
                      Export PDF
                    </Button>
                    <Button
                      className="declarations-export-button"
                      size="small"
                      onClick={handleOpenRelayPdfModal}
                      disabled={
                        declarationsReportLoading ||
                        declarationsReportSwimmers.length === 0 ||
                        !hasRelayAvailabilityData
                      }
                    >
                      Relay Availability PDF
                    </Button>
                    <Button
                      size="small"
                      disabled={
                        statusCounts.noResponse === 0 ||
                        meetIsPublishedOrLater ||
                        declarationsLocked
                      }
                      onClick={handleOpenEmailNoResponse}
                    >
                      Email no response families ({statusCounts.noResponse})
                    </Button>
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    opacity: declarationsLocked ? 0.5 : 1,
                  }}
                >
                  <Table<IDeclarationsReportSwimmer>
                    size="small"
                    className="meet-declarations-table"
                    rowKey={(row) => row.swimmerId}
                    loading={declarationsReportLoading}
                    dataSource={sortedForDisplay}
                    rowClassName={(record) => {
                      const status = deriveDeclarationStatus(record)
                      if (status === "attending") return "declarations-row-attending"
                      if (status === "notAttending") return "declarations-row-not-attending"
                      return ""
                    }}
                    pagination={false}
                    scroll={{ x: 800 }}
                    columns={[
                      {
                        title: "Swimmer",
                        key: "name",
                        sorter: (a, b) => {
                          const nameA = `${a.last_name} ${a.first_name}`.toLowerCase()
                          const nameB = `${b.last_name} ${b.first_name}`.toLowerCase()
                          if (nameA < nameB) return -1
                          if (nameA > nameB) return 1
                          return 0
                        },
                        render: (s: IDeclarationsReportSwimmer) => (
                          <span>
                            {s.last_name}, {s.first_name}
                          </span>
                        ),
                      },
                      {
                        title: "Gender",
                        dataIndex: "gender",
                        key: "gender",
                        filters: [
                          { text: "Girls", value: "Girl" },
                          { text: "Boys", value: "Boy" },
                        ],
                        onFilter: (value, record) => record.gender === value,
                      },
                      {
                        title: "League Age",
                        dataIndex: "leagueAge",
                        key: "leagueAge",
                        sorter: (a, b) => a.leagueAge - b.leagueAge,
                      },
                      {
                        title: "Status",
                        key: "status",
                        filters: [
                          { text: "Attending", value: "attending" },
                          {
                            text: "Not attending",
                            value: "notAttending",
                          },
                          {
                            text: "No response",
                            value: "noResponse",
                          },
                        ],
                        onFilter: (value, record) =>
                          deriveDeclarationStatus(
                            record as IDeclarationsReportSwimmer,
                          ) === value,
                        sorter: (a, b) => {
                          const statusA = deriveDeclarationStatus(a)
                          const statusB = deriveDeclarationStatus(b)
                          if (statusA === statusB) return 0
                          const order: Record<string, number> = {
                            attending: 0,
                            notAttending: 1,
                            noResponse: 2,
                          }
                          return order[statusA] - order[statusB]
                        },
                        render: (s: IDeclarationsReportSwimmer) => {
                          const status = deriveDeclarationStatus(s)
                          if (status === "attending") return "Attending"
                          if (status === "notAttending") return "Not attending"
                          return "No response"
                        },
                      },
                      {
                        title: "Ind. events entered",
                        key: "events",
                        render: (s: IDeclarationsReportSwimmer) => {
                          const eventsCount =
                            s.elligibleEvents?.filter(
                              (evt) =>
                                evt.isRelay !== 1 && evt.enteredInEvent === 1,
                            ).length || 0
                          return eventsCount
                        },
                      },
                      {
                        title: "Relay availability",
                        key: "relayAvailability",
                        render: (s: IDeclarationsReportSwimmer) => {
                          const relayEvents =
                            s.elligibleEvents?.filter((evt) => evt.isRelay === 1) || []

                          if (relayEvents.length === 0) {
                            return (
                              <span style={{ fontSize: 11, color: "#999" }}>
                                No relay events
                              </span>
                            )
                          }
                          const lines = relayEvents.map((evt) => {
                            let label = `#${evt.eventNumber} ${evt.age_group} ${evt.eventGender} ${evt.distance} ${evt.stroke}`
                            let statusLabel = "No response"

                            if (evt.relayAvailable === 1) statusLabel = "Available"
                            else if (evt.relayAvailable === 0) statusLabel = "Unavailable"

                            return `${label}: ${statusLabel}`
                          })

                          return (
                            <div style={{ fontSize: 11, whiteSpace: "normal" }}>
                              {lines.map((line, idx) => (
                                <div key={idx}>{line}</div>
                              ))}
                            </div>
                          )
                        },
                      },
                      {
                        title: "Parent comments",
                        key: "comments",
                        render: (s: IDeclarationsReportSwimmer) => {
                          if (!s.comments || s.comments.trim() === "") {
                            return (
                              <span style={{ fontSize: 11, color: "#999" }}>
                                
                              </span>
                            )
                          }

                          const text =
                            s.comments.length > 140
                              ? `${s.comments.substring(0, 137)}...`
                              : s.comments

                          return <span style={{ fontSize: 11 }}>{text}</span>
                        },
                      },
                      {
                        title: "Last updated",
                        key: "lastUpdated",
                        render: (s: IDeclarationsReportSwimmer) => {
                          const text = formatDeclarationAudit(s)
                          return text ? (
                            <span style={{ fontSize: 9 }}>{text}</span>
                          ) : (
                            <span style={{ fontSize: 9, color: "#999" }}>
                              
                            </span>
                          )
                        },
                      },
                    ]}
                    // When declarations are locked, table is read-only; we
                    // already avoid rendering any interactive cell controls.
                  />
                </div>
              </>
            </div>
          )
        ) : null}

        {declarationsLocked && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              cursor: "not-allowed",
              zIndex: 2,
            }}
          />
        )}
      </div>

      <Modal
        title="Relay Availability PDF"
        open={relayPdfModalVisible}
        okText="Download Relay Availability PDF"
        onOk={handleDownloadRelayPdf}
        onCancel={() => setRelayPdfModalVisible(false)}
        okButtonProps={{
          disabled:
            relayAgeGroupOptions.length === 0 || relaySelectedAgeGroups.length === 0,
        }}
      >
        <p style={{ fontSize: 14 }}>
          This report includes only swimmers who are marked as
          <strong> attending</strong> and have relay availability recorded.
        </p>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Relay gender</div>
          <Radio.Group
            value={relayGenderFilter}
            onChange={(e) => setRelayGenderFilter(e.target.value as RelayGenderFilter)}
          >
            <Radio.Button value="both">Both</Radio.Button>
            <Radio.Button value="girls">Girls</Radio.Button>
            <Radio.Button value="boys">Boys</Radio.Button>
          </Radio.Group>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Age groups</div>
          {relayAgeGroupOptions.length === 0 ? (
            <p style={{ fontSize: 13, color: "#888" }}>
              No relay age groups found for this meet.
            </p>
          ) : (
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #eee",
                padding: 8,
                borderRadius: 4,
              }}
            >
              <Checkbox.Group
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
                options={relayAgeGroupOptions}
                value={relaySelectedAgeGroups}
                onChange={(values) =>
                  setRelaySelectedAgeGroups(values as string[])
                }
              />
            </div>
          )}
          <p style={{ fontSize: 12, marginTop: 8, color: "#888" }}>
            Age groups default to all available relay age groups for this meet.
          </p>
        </div>
      </Modal>

      <Modal
        title="Email no response families"
        open={emailModalVisible}
        okText="Send reminder emails"
        confirmLoading={emailSending}
        okButtonProps={{
          disabled: isProdLikeClientEnv && atReminderLimit,
          title:
            isProdLikeClientEnv && atReminderLimit
              ? "You have already used all reminder email sends for this meet in production."
              : undefined,
        }}
        onOk={handleSendEmailNoResponse}
        onCancel={() => setEmailModalVisible(false)}
      >
        <p style={{ fontSize: 14 }}>
          You’re about to email parent/guardians of swimmers who have <strong>not yet
          responded</strong> for this meet. They’ll receive a reminder with a direct
          link to complete declarations.
        </p>
        {emailDryRunSummary && (
          <>
            <p style={{ fontSize: 14 }}>
              {atReminderLimit ? (
                <>
                  You have already used {maxRemindersPerMeet} of {maxRemindersPerMeet} reminder
                  email sends for this swim meet.
                </>
              ) : (
                <>
                  {remindersSentCount} of {maxRemindersPerMeet} reminder emails have already been
                  sent. You have {remainingReminders} remaining for this meet.
                </>
              )}
            </p>
            {isNonProdClientEnv && (
              <p style={{ fontSize: 14 }}>
                Limits only apply in production; testing environments are not limited.
              </p>
            )}
          </>
        )}
        {!emailDryRunSummary && (
          <p style={{ fontSize: 14, color: "#888" }}>
            Calculating recipient count…
          </p>
        )}
        {isNonProdClientEnv && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: 14 }}>
              In testing environments, you can direct these reminder emails to a
              single QA address instead of real parents. Leave blank to use the
              default override address.
            </p>
            <Input
              placeholder="Optional test recipient email (non-production only)"
              value={testOverrideEmail}
              onChange={(e) => setTestOverrideEmail(e.target.value)}
            />
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={allowReplies}
            onChange={(e) => setAllowReplies(e.target.checked)}
          >
            Allow replies to my email
          </Checkbox>
          <p style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            Email still sends from Swimmingly no-reply. When enabled, pressing
            Reply goes to your email. Copied admins are CC'd (visible). When
            disabled, copied admins are BCC'd (hidden).
          </p>
          <Checkbox
            checked={copyOtherClubAdmins}
            onChange={(e) => handleCopyAdminsToggle(e.target.checked)}
          >
            Copy other club admins
          </Checkbox>
          {copyOtherClubAdmins && (
            <div style={{ marginTop: 8 }}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                loading={adminCopyLoading}
                value={adminCopyUserIds}
                onChange={(values) => setAdminCopyUserIds(values as number[])}
                options={adminCopyOptions}
                showSearch={{ optionFilterProp: "label" }}
                placeholder="Select league/club admins to copy"
              />
            </div>
          )}
        </div>
        {declarationsLocked && (
          <p style={{ fontSize: 14, color: "#999", marginTop: 8 }}>
            Meet declarations are locked because entries are no longer in the
            Editing Entries state.
          </p>
        )}
      </Modal>
    </>
  )
}

export default MeetDeclarationsTab
